import { useTranslation } from "react-i18next";
import { Spinner } from "@boligportal/bricks-ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { type ReportView } from "~/api/generated/model";
import { powerBiQueries } from "~/api/powerbi/queries";
import { hasPermission } from "~/hooks/useHasPermission";
import { ReportEmbed } from "../-components/ReportEmbed";
import { SLUGS, type Slug } from "../-components/reports";

export const Route = createFileRoute("/(restricted)/rapporter/$reportSlug/")({
  component: RouteComponent,
  beforeLoad: ({ context: { authClient } }) => {
    const hasReportsPermission = hasPermission(authClient.user, "reports");

    if (!hasReportsPermission) {
      throw redirect({ to: "/rapporter" });
    }
  },
  loader: async ({ context: { i18n }, params: { reportSlug } }) => {
    await i18n.loadNamespaces("analyse");

    const title = SLUGS.includes(reportSlug as Slug)
      ? i18n.t(`analyse:slugs.${reportSlug as Slug}.title`)
      : i18n.t("analyse:meta.title");

    return { meta: { title } };
  },
  head: async (context) => {
    return {
      meta: [
        {
          title: context.loaderData?.meta.title,
        },
      ],
    };
  },
});

function ReportEmbedLoader({ report }: { report: ReportView }) {
  const { t } = useTranslation("analyse");
  const { data: embedUrlData, isLoading: isUrlLoading } = useQuery(
    powerBiQueries.embedUrl(report.id),
  );
  const { data: tokenData, isLoading: isTokenLoading } = useQuery(
    powerBiQueries.embedToken(report.id),
  );

  if (isUrlLoading || isTokenLoading) {
    return (
      <div className="text-primary-administration flex h-full w-full items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (!embedUrlData || !tokenData) {
    return (
      <div className="text-danger flex h-full w-full items-center justify-center">
        {t("error")}
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <ReportEmbed
        embedUrl={embedUrlData.embedUrl}
        token={tokenData.token}
        tokenExpiration={tokenData.tokenExpiration}
        reportId={report.id}
      />
    </div>
  );
}

function RouteComponent() {
  const { reportSlug } = Route.useParams();
  const { data: reports, isLoading } = useQuery(powerBiQueries.reports());
  const { t } = useTranslation("analyse");

  if (isLoading) {
    return (
      <div className="bg-background flex w-full items-center justify-center py-10">
        <div className="text-muted-foreground text-sm">{t("loading")}</div>
      </div>
    );
  }

  const report = reports?.find((r) => r.slug === reportSlug);

  if (!report) {
    throw notFound();
  }

  return (
    <div className="bg-background flex h-full w-full">
      <ReportEmbedLoader report={report} />
    </div>
  );
}
